import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeConfirmationModal } from '../../reducers/uiSlice';
import { confirmationService } from '../../services/confirmationService';
import { ICONS } from '../../constants/imageConstants';
import { Button } from '../buttons/button';
import { cn } from '../../lib/utils';
import BaseModal from '../modals/BaseModal';
import { SyncConfirmIcon } from '../Icons/svgIcons';

const ConfirmationModal = () => {
  const dispatch = useDispatch();
  const { isOpen, title, message, type, confirmId } = useSelector(state => state.ui.confirmationModal);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = () => {
    if (confirmId && !isLoading) {
      setIsLoading(true);
      const actionPromise = confirmationService.execute(confirmId);

      if (actionPromise && typeof actionPromise.finally === 'function') {
        actionPromise.finally(() => {
          setIsLoading(false);
          dispatch(closeConfirmationModal());
          confirmationService.cleanup(confirmId);
        });
      } else {
        setIsLoading(false);
        dispatch(closeConfirmationModal());
        confirmationService.cleanup(confirmId);
      }
    }
  };

  const handleCancel = () => {
    if (confirmId) confirmationService.cancel(confirmId);
    dispatch(closeConfirmationModal());
  };

  const isDestructiveAction = type === 'destructive' || type === 'logout' || type === 'danger';
  const isHighImpact = type === 'danger' || type === 'logout';

  const getIcon = () => {
    switch (type) {
      case 'destructive':
      case 'danger':
        return ICONS.TRASH;
      case 'logout':
        return ICONS.LOGOUT_MODAL;
      case 'sync':
        return <SyncConfirmIcon />;
      default:
        return ICONS.LOGOUT_MODAL;
    }
  };

  const getIconBgColor = () => {
    switch (type) {
      case 'destructive':
      case 'logout':
      case 'danger':
        return '#cf002926';
      case 'sync':
        return '#3B82F626';
      default:
        return '#FBBF2426';
    }
  };

  const getConfirmButtonText = () => {
    switch (type) {
      case 'destructive':
      case 'danger':
        return 'Yes, Delete';
      case 'logout':
        return 'Yes, Logout';
      default:
        return 'Confirm';
    }
  };

  const getSubtitle = () => {
    return (
      <span className={cn(
        "text-sm leading-relaxed",
        isHighImpact ? "text-destructive font-medium" : "text-gray"
      )}>
        {message || 'Are you sure you want to proceed with this action?'}
      </span>
    );
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      subtitle={getSubtitle()}
      icon={getIcon()}
      iconBgColor={getIconBgColor()}
    >
      <div className="flex gap-3">
        <Button 
          onClick={handleCancel} 
          disabled={isLoading} 
          variant="outline" 
          size="default" 
          className="flex-1"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          disabled={isLoading} 
          className="flex-1"
          variant={isDestructiveAction ? 'danger' : 'primary'}
        >
          {isLoading ? (
            <div className="animate-spin rounded-full w-5 h-5 border-b-2 border-white"></div>
          ) : getConfirmButtonText()}
        </Button>
      </div>
    </BaseModal>
  );
};

export default ConfirmationModal;